// ============================================================
// GateElevation.js
// Inline SVG CUT SHEET for one chain link gate leaf — the welded frame only.
//
// This is fabrication line art for the shop, not an installation detail:
// there are no posts, no dome caps, no ground line, and no opening/height
// dimensions. What the welder needs is the two cut lengths, so those are the
// only dimensions on the sheet.
//
// All member lengths come from computeGateCuts; this file only maps inches to
// viewBox units. NOT TO SCALE — the frame is fitted to the frame of the page.
//
// Pure black, 1px and thinner, no fills, so it reproduces on any copier.
// ============================================================

import React, { useId } from 'react';
import { computeGateCuts } from '../utils/gateDrawing';

// Canvas geometry, in viewBox units.
const VIEW_W = 600;
const VIEW_H = 400;
const PAD_TOP = 55;    // room for the width dimension above
const PAD_RIGHT = 70;  // room for the upright dimension on the right
const PAD_LEFT = 40;
const PAD_BOTTOM = 40;
const DRAW_W = VIEW_W - PAD_LEFT - PAD_RIGHT;   // 490
const DRAW_H = VIEW_H - PAD_TOP - PAD_BOTTOM;   // 305

const INK = '#000000';
const UPRIGHT_PROUD_IN = 1; // uprights stand 1" proud of the rails, per the cut rules

// Line weights.
const W_MEMBER = 1;
const W_MESH = 0.4;
const W_DIM = 0.5;
const W_BARB = 0.5;

// Barbed wire: 3 strands, an X-shaped barb roughly every 20px, each barb about
// 3px across.
const BARB_STRANDS = 3;
const BARB_SPACING = 20;
const BARB_SIZE = 3;

// A cantilever can be 40 ft long, which drives the scale so low that a 2" tube
// would collapse to a single hairline. Members never draw thinner than this, so
// the double-line tube always reads.
const MIN_TUBE_PX = 2;
// Each barb arm continues DOWN past the top rail as a mounting leg, so the
// hang side is legible: on the near face the leg runs visibly down the frame,
// on the far face the rail and frame hide it. 6in at gate scale, but never
// less than the rail thickness plus a clear margin.
// Gate stops: short lengths of pipe welded upright on the two END uprights,
// projecting above the top rail. Cantilever only — a swing leaf has none.
const GATE_STOP_IN = 2;
const ARM_LEG_IN = 6;
const ARM_LEG_MIN_CLEARANCE = 6;
// The leg sits just BESIDE the upright, not on it, so it never crosses one.
const ARM_LEG_GAP = 2;
// How far an arm reaches sideways per unit of rise.
//
// This was 1 — a 45 degree arm. At 45 degrees the three strands step sideways
// by a third of the extension each, and the assembly reads as a slanted BAND
// across the top of the gate, which on a flat elevation looks like a drawing in
// perspective. It is not: an elevation has no depth, and a shape that implies it
// is simply wrong.
//
// A real barb arm stands much closer to upright than that. At 0.45 the arm
// leans about 24 degrees off vertical, the strands stack in a narrow column
// that is plainly three separate wires between plainly separate arms, and the
// lean still reads unmistakably in one direction.
const BARB_LEAN_RATIO = 0.45;
// Barb arms are a real member with two walls, not a hairline: on the owner's
// photograph they are pipe, and drawn as a single stroke they disappeared into
// the strands. 1in at gate scale, floored like every other tube.
const BARB_ARM_OD_IN = 1;

/**
 * `x`, `y`, `width`, `height` are only needed when this is nested inside
 * another <svg> (see GateSheet). Left at their defaults it renders standalone,
 * full-width, exactly as before — the drawing itself is unaffected either way,
 * since the internal 600x400 viewBox does all the scaling.
 *
 * `hideDimensions` drops the width and upright dimensions — lines, ticks
 * and labels — for the blank-sheet template, where the shop writes the
 * measurements by hand. The FRAME is unaffected: members, mesh, and barbed wire
 * are geometry, not measurements, and still draw exactly as they otherwise
 * would. Defaults to false, so every existing caller is unchanged.
 */
export default function GateElevation({ gate, x, y, width = '100%', height, hideDimensions = false }) {
  const cuts = computeGateCuts(gate);
  // Unique per instance so two drawings on one page don't share defs.
  const uid = `gate-${useId().replace(/:/g, '')}`;
  const meshId = `${uid}-mesh`;
  const clipId = `${uid}-clip`;

  // ── CANTILEVER ──────────────────────────────────────────────────────────
  // A slide gate shares nothing structurally with a swing leaf, so it gets its
  // own render path rather than bending the swing geometry to fit. Every hook
  // has already run above, so returning here is safe.
  if (cuts.isCantilever) {
    return renderCantilever({ cuts, uid, meshId, clipId, x, y, width, height, hideDimensions, gate });
  }

  const { frameOdIn, railCutIn, uprightCutIn, overallWidthIn, leafCount, barbedWire, barbExtensionIn } = cuts;

  // A double swing draws its two identical leaves side by side, separated by a
  // purely visual gap about one tube wide. The gap represents nothing measured
  // and never carries a dimension — it just stops the two frames reading as one.
  const isDouble = leafCount === 2;
  const gapIn = frameOdIn;
  const contentWidthIn = isDouble ? overallWidthIn * 2 + gapIn : overallWidthIn;

  // ── WIDTH DIMENSION, ABOVE OR BELOW ──────────────────────────────────
  //
  // ACTUAL SIZE puts the OTO width BELOW the frame and the welder's RAIL CUT
  // PIECE above it, so the two read as two separate measurements instead of one
  // stacked pair. Both copies get the lower dimension: the customer is
  // approving that outside measurement and has to see it before signing, and
  // the cut piece stays shop-only (showRailCutPiece is already false on a
  // customer copy).
  //
  // A NORMAL gate is unchanged: its rail-cut dimension stays above the frame,
  // and a customer copy of one still carries no width dimension at all.
  const showWidthBelow = !!cuts.actualSize && !hideDimensions;
  // Room the lower dimension needs under the frame: the line, then its label.
  const BELOW_BAND = 38;
  // The frame's vertical budget, less that band when it is in use. The SCALE is
  // taken from this, so the whole drawing shrinks uniformly — the 3:2 viewBox
  // and the frame's own proportions are untouched. Nothing is ever stretched.
  const availH = DRAW_H - (showWidthBelow ? BELOW_BAND : 0);

  // Fit the whole content — both leaves and the gap — to the drawing area;
  // whichever axis is tighter sets the scale. For a single, contentWidthIn is
  // just overallWidthIn, so singles scale and sit exactly as they did before.
  const scale = (contentWidthIn > 0 && uprightCutIn > 0)
    ? Math.min(DRAW_W / contentWidthIn, availH / uprightCutIn)
    : 1;

  const frameW = overallWidthIn * scale;   // ONE leaf
  const contentW = contentWidthIn * scale; // both leaves plus the gap
  const frameH = uprightCutIn * scale;
  const od = frameOdIn * scale;            // member thickness on the page
  const gap = gapIn * scale;
  const proud = UPRIGHT_PROUD_IN * scale;  // 1" overhang, scaled

  // Origin = top-left of the content, centred in the drawing area.
  const ox = PAD_LEFT + (DRAW_W - contentW) / 2;
  // Centred in the budget the frame actually has, so reserving the lower band
  // lifts the frame rather than pushing the dimension off the bottom.
  const oy = PAD_TOP + (availH - frameH) / 2;

  // Left edge of each leaf. Both leaves are identical, so only x differs.
  const leafOriginXs = isDouble ? [ox, ox + frameW + gap] : [ox];

  // Vertical geometry — shared by every leaf.
  //
  // Barbed wire lengthens the UPRIGHTS only. The fence portion — top rail, mesh,
  // mid rail, bottom rail — is unchanged and stays at the base height; the
  // extension rises above the top rail, which is where the mesh ends. So every
  // rail is positioned from the bottom of the frame or from the top of the FENCE
  // zone, never from the top of the extended upright.
  const barbExtension = barbExtensionIn * scale; // 0 when not barbed
  const fenceTopY = oy + barbExtension;          // top of the fence zone
  const fenceH = frameH - barbExtension;

  const railW = railCutIn * scale;
  const topRailY = fenceTopY + proud;
  const bottomRailY = oy + frameH - proud - od;
  const midRailY = fenceTopY + fenceH / 2 - od / 2;
  const vMidTop = topRailY + od;
  const vMidH = Math.max(0, bottomRailY - vMidTop);
  const meshY = topRailY + od;
  const meshH = Math.max(0, bottomRailY - meshY);

  // ── HORIZONTAL RAIL POSITIONS ──
  //
  // The first and last are the top and bottom rails; anything between them is
  // spaced evenly, the way the verticals already are.
  //
  // The two rule cases keep their ORIGINAL expressions rather than going through
  // the general formula: midRailY is algebraically the midpoint of top and
  // bottom, but `a + (b - a) / 2` and `fenceTopY + fenceH / 2 - od / 2` can
  // differ in the last bit of a float, and that would move every existing
  // three-rail drawing by a millionth of a unit. Same numbers, same output.
  const railYs = (() => {
    const count = cuts.horizontalRailCount;
    if (count <= 0) return [];
    if (count === 1) return [topRailY];
    if (count === 2) return [topRailY, bottomRailY];
    if (count === 3) return [topRailY, midRailY, bottomRailY];
    const step = (bottomRailY - topRailY) / (count - 1);
    return Array.from({ length: count }, (_, i) => (
      i === 0 ? topRailY : i === count - 1 ? bottomRailY : topRailY + step * i
    ));
  })();
  // Top and bottom first, then the intermediates — the order the rails have
  // always been emitted in, so an un-overridden sheet is unchanged.
  const endRailYs = railYs.length >= 2 ? [railYs[0], railYs[railYs.length - 1]] : railYs;
  const midRailYs = railYs.slice(1, Math.max(1, railYs.length - 1));

  // 2x4 nailers. Each board is centred on its rail so it reads as bolted to
  // the rail's face, and spans exactly the rail — x and width are the rail's
  // own — so a board can never reach past an upright.
  //
  // The centred position is then clamped into the FENCE zone: never above
  // fenceTopY, which keeps a board clear of the barbed wire extension (and,
  // on an unbarbed gate, level with the top of the frame), and never below the
  // bottom of the frame. The clamp only ever bites by a fraction of an inch —
  // it is a guard, not a layout rule — so no padding or scale changes.
  const nailerT = (cuts.nailerFaceIn || 0) * scale;
  const nailerY = (railY) => Math.min(
    Math.max(railY + od / 2 - nailerT / 2, fenceTopY),
    oy + frameH - nailerT
  );

  // Every member is a tube drawn as an outlined rectangle — its two long edges
  // read as the double lines of the tube wall.
  const member = (x, y, w, h, key) => (
    <rect
      key={key}
      x={x}
      y={y}
      width={Math.max(0, w)}
      height={Math.max(0, h)}
      fill="none"
      stroke={INK}
      strokeWidth={W_MEMBER}
    />
  );

  /**
   * The 3 barbed wire strands in the extension zone, with their barbs.
   *
   * Strands run from the INSIDE face of the left upright to the inside face of
   * the right upright — the same span as the rails — so no strand pixel ever
   * falls within a tube. Barbs are inset a further half-barb at each end so the
   * X marks stay inside the strand too; nothing is drawn over a member and then
   * covered up.
   */
  const renderBarbedWire = (lx) => {
    if (!barbedWire) return null;
    const x0 = lx + od;                 // inside face, left upright
    const x1 = lx + frameW - od;        // inside face, right upright
    const span = x1 - x0;
    if (span <= 0) return null;

    // Evenly spaced across the band between the top of the uprights and the
    // top rail, with a gap above and below rather than touching either.
    const bandTop = oy;
    const bandBottom = topRailY;
    const step = (bandBottom - bandTop) / (BARB_STRANDS + 1);

    const half = BARB_SIZE / 2;
    const usable = span - BARB_SIZE;    // keeps every X fully inside the strand
    const barbCount = usable > 0 ? Math.max(1, Math.floor(span / BARB_SPACING)) : 0;

    return (
      <g>
        {Array.from({ length: BARB_STRANDS }).map((_, s) => {
          const y = bandTop + step * (s + 1);
          return (
            <g key={`strand-${s}`}>
              <line x1={x0} y1={y} x2={x1} y2={y} stroke={INK} strokeWidth={W_BARB} />
              {Array.from({ length: barbCount }).map((__, b) => {
                const cx = x0 + half + (usable * (b + 0.5)) / barbCount;
                return (
                  <g key={`barb-${b}`}>
                    <line x1={cx - half} y1={y - half} x2={cx + half} y2={y + half} stroke={INK} strokeWidth={W_BARB} />
                    <line x1={cx - half} y1={y + half} x2={cx + half} y2={y - half} stroke={INK} strokeWidth={W_BARB} />
                  </g>
                );
              })}
            </g>
          );
        })}
      </g>
    );
  };

  /**
   * One complete leaf at the given left edge. Both leaves of a double are
   * identical — same cuts, same members, same rules — so this is called once
   * per leaf rather than the member code being repeated.
   */
  const renderLeaf = (lx, i) => (
    <g key={`leaf-${i}`}>
      {/* Mesh, clipped to THIS leaf's opening so the diamonds stop at its own
          frame and never cross the gap between leaves.

          FRAME ONLY drops the fill and nothing else. The clip path is still
          defined in <defs> and every coordinate below is untouched, so the
          frame this draws is the same frame either way — the shop cuts and
          welds identically and just never hangs the mesh. */}
      {!cuts.frameOnly && (
        <rect
          x={lx + od}
          y={meshY}
          width={Math.max(0, railW)}
          height={meshH}
          fill={`url(#${meshId})`}
          clipPath={`url(#${clipId}-${i})`}
        />
      )}

      {/* Uprights — the side members, full cut length */}
      {member(lx, oy, od, frameH)}
      {member(lx + frameW - od, oy, od, frameH)}

      {/* Top and bottom rails — welded between the uprights, 1" in from each end */}
      {endRailYs.map((railY, r) => member(lx + od, railY, railW, od, 'rail-end-' + r))}

      {/* Intermediate horizontal rails — the mid rail the height rule calls for,
          or however many a count override asks for, spaced evenly between the
          top and bottom rails. */}
      {midRailYs.map((railY, r) => member(lx + od, railY, railW, od, 'rail-mid-' + r))}

      {/* 2x4 nailers — top rail, bottom rail, and the mid rail when there is
          one. Drawn with a WHITE fill so each board occludes the tube behind
          it, the way the cantilever members occlude each other, rather than
          reading as an outline laid over a visible rail.

          Deliberately drawn BEFORE the verticals and the diagonals below, so
          the bracing still reads over the boards and a diagonal is still seen
          landing on its rail rather than dying into a plank. Nothing here
          moves a diagonal: their endpoints are computed off the rails and are
          untouched by this. */}
      {/* The MID RAIL's board is its own switch: wood infill wants all three,
          PVC wants top and bottom only. Unticking it removes that board and
          nothing else — the rail itself, the other boards and every cut stay
          exactly as they were. */}
      {cuts.nailers2x4 && [
        ...(railYs.length >= 1 ? [['top', railYs[0]]] : []),
        ...(cuts.nailerMidRail ? midRailYs.map((y, r) => ['mid-' + r, y]) : []),
        ...(railYs.length >= 2 ? [['bottom', railYs[railYs.length - 1]]] : []),
      ].map(([name, railY]) => (
        <rect
          key={'nailer-' + name}
          x={lx + od}
          y={nailerY(railY)}
          width={Math.max(0, railW)}
          height={Math.max(0, nailerT)}
          fill="#FFFFFF"
          stroke={INK}
          strokeWidth={W_MEMBER}
        />
      ))}

      {/* Vertical mid rails — none, one centred, or two at the thirds. The
          fraction i/(count+1) gives 1/2 for one and 1/3, 2/3 for two. */}
      {Array.from({ length: cuts.verticalMidRailCount }).map((_, v) => (
        member(
          lx + (frameW * (v + 1)) / (cuts.verticalMidRailCount + 1) - od / 2,
          vMidTop, od, vMidH, 'vmid-' + v
        )
      ))}

      {/* DIAGONALS — two per leaf, both in the LOWER portion, in the OUTER
          bays, rising toward the centre. The centre bay and the whole upper
          half are deliberately left open, per the shop's sketch.

          They run from the bottom inside corners up to where the verticals
          meet the upper boundary — the underside of the horizontal mid rail
          when there is one, otherwise the underside of the top rail. Every
          endpoint lands on a member's face, so a diagonal never crosses
          through an upright, a rail or a vertical.

          Diagonals only appear over 15 ft, which is exactly when the leaf has
          its two verticals, so both brace targets always exist. */}
      {cuts.hasDiagonals && cuts.verticalMidRailCount === 2 && (() => {
        const insideL = lx + od;                     // inside face, left upright
        const insideR = lx + frameW - od;            // inside face, right upright
        const lowerY = bottomRailY;                  // top face of the bottom rail
        const upperY = cuts.hasMidRail ? midRailY + od : topRailY + od;
        const v1 = lx + frameW / 3;                  // first vertical, centre
        const v2 = lx + (frameW * 2) / 3;            // second vertical, centre
        const dw = Math.max(W_DIM, Math.min(W_MEMBER, cuts.diagonalOdIn * scale));
        if (upperY >= lowerY) return null;
        return (
          <g>
            <line x1={insideL} y1={lowerY} x2={v1 - od / 2} y2={upperY} stroke={INK} strokeWidth={dw} />
            <line x1={insideR} y1={lowerY} x2={v2 + od / 2} y2={upperY} stroke={INK} strokeWidth={dw} />
          </g>
        );
      })()}

      {/* Barbed wire — every leaf gets the extension and its strands. */}
      {renderBarbedWire(lx)}
    </g>
  );

  // The width dimension is called out ONCE, over the LEFT leaf only — both
  // leaves are identical, and dimensioning the pair would imply an overall
  // width the welder was never given.
  //
  // WHAT it spans switches on ACTUAL SIZE, because the two modes answer
  // different questions. A normal gate is dimensioned across the RAIL: the
  // welder is cutting a rail, and the deduction that produced that length is
  // the point of the sheet. An actual-size gate is dimensioned OUTSIDE FACE to
  // OUTSIDE FACE of the two uprights — the left upright's outer face is the
  // leaf origin, the right upright's is one leaf width along — because there
  // the user entered a finished outside measurement in the first place.
  // ABOVE the frame — the rail-cut dimension of a NORMAL gate, across the rail
  // itself. Unchanged, and now the only thing that dimension line ever shows:
  // under actual size the width moved below (see showWidthBelow above).
  const dimTopY = oy - 24;
  const railDimX0 = ox + od;
  const railDimX1 = ox + od + railW;
  const showWidthAbove = !hideDimensions && !cuts.customerCopy && !cuts.actualSize;

  // BELOW the frame — the OTO width of an ACTUAL SIZE gate, outside face to
  // outside face: the left upright's outer face is the leaf origin, the right
  // upright's is one leaf width along. Its own line and ticks, clear of the
  // frame, so it reads as a measurement in its own right.
  const otoDimX0 = ox;
  const otoDimX1 = ox + frameW;
  const otoDimY = oy + frameH + 20;
  // On a double the entered width is PER LEAF and this dimension spans the left
  // leaf only, so the label says so. Without it a customer signing for a pair
  // could read one leaf's width as the width of the whole gate.
  const otoLabelText = isDouble ? `${cuts.otoLabel} (per leaf)` : cuts.otoLabel;
  // The welder's number, ABOVE the frame where the dimension used to be. A
  // callout, not a dimension — it measures no distance on the drawing.
  const cutPieceY = oy - 18;

  // ── SCREEN-READER DESCRIPTION ────────────────────────────────────────
  //
  // One sentence, because it is read ALOUD rather than scanned.
  //
  // It is built from the same flags that decide what gets DRAWN, so it can only
  // ever describe what is actually on the sheet:
  //   • a blank template carries no numbers, so it announces none;
  //   • a normal gate is dimensioned across the rail, and a customer copy of
  //     one carries no width dimension at all — so neither does its sentence;
  //   • an actual-size gate gives the outside width, plus the rail cut piece on
  //     a SHOP copy only, exactly as printed;
  //   • barbed wire is spoken as the split the drawing shows.
  //
  // It used to say "rail cut to 48"" for an actual-size gate, describing the
  // entered OUTSIDE measurement as a rail cut — the same confusion that was
  // corrected in print. Under actual size railCutLabel IS that outside figure,
  // so here it is named as one.
  const leafPhrase = isDouble ? 'two identical leaves' : 'one leaf';
  const perLeaf = isDouble ? ' per leaf' : '';
  const heightPhrase = barbedWire
    ? `upright ${cuts.uprightCutLabel}, drawn as ${cuts.fenceHeightLabel} plus ${cuts.barbHeightLabel} of barbed wire`
    : `upright ${cuts.uprightCutLabel}`;
  const sayings = [];
  if (hideDimensions) {
    sayings.push('dimensions left blank to be filled in by hand');
  } else {
    if (showWidthAbove) sayings.push(`rail cut to ${cuts.railCutLabel}`);
    if (showWidthBelow) sayings.push(`${cuts.railCutLabel} outside width${perLeaf}`);
    if (cuts.showRailCutPiece) sayings.push(`rail cut piece ${cuts.railCutPieceLabel}${perLeaf}`);
    sayings.push(heightPhrase);
  }
  const swingAriaLabel =
    `${gate?.type || 'Gate'} ${cuts.customerCopy ? 'gate drawing' : 'frame cut sheet'}, ${leafPhrase}: ${sayings.join(', ')}.`;
  // The upright dimension sits once, past the right edge of the whole drawing.
  const dimRightX = ox + contentW + 30;

  return (
    <svg
      x={x}
      y={y}
      width={width}
      height={height}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      role="img"
      aria-label={swingAriaLabel}
      style={{ display: 'block', fontFamily: 'Manrope, sans-serif' }}
    >
      <defs>
        {/* Chain link diamonds — two crossing diagonals, no background fill. */}
        <pattern id={meshId} width="8" height="8" patternUnits="userSpaceOnUse">
          <path d="M0,8 L8,0" stroke={INK} strokeWidth={W_MESH} strokeOpacity="0.65" fill="none" />
          <path d="M0,0 L8,8" stroke={INK} strokeWidth={W_MESH} strokeOpacity="0.65" fill="none" />
        </pattern>
        {/* Mesh is clipped per leaf so it can't spill over the members or
            cross the gap between a double's two leaves. */}
        {leafOriginXs.map((lx, i) => (
          <clipPath key={i} id={`${clipId}-${i}`}>
            <rect x={lx + od} y={meshY} width={Math.max(0, railW)} height={meshH} />
          </clipPath>
        ))}
      </defs>

      {/* One leaf for a single, two identical leaves for a double */}
      {leafOriginXs.map((lx, i) => renderLeaf(lx, i))}

      {/* ── Width, ABOVE the frame: a NORMAL gate's rail-cut dimension ──
          Over the LEFT leaf only — both leaves are identical, and dimensioning
          the pair would imply an overall width the welder was never given.
          Ticks land on the rail ends, because the rail is what is being cut and
          the deduction that produced its length is the point of the sheet.

          Dropped on a CUSTOMER COPY: a rail cut means nothing to the person
          approving how the gate will LOOK, and the data column carries the size.
          Dropped under ACTUAL SIZE too — that gate's width is dimensioned below
          the frame instead, so this line never carries two meanings. */}
      {showWidthAbove && (
      <>
      <line x1={railDimX0} y1={dimTopY} x2={railDimX1} y2={dimTopY} stroke={INK} strokeWidth={W_DIM} />
      <line x1={railDimX0} y1={dimTopY - 5} x2={railDimX0} y2={dimTopY + 5} stroke={INK} strokeWidth={W_DIM} />
      <line x1={railDimX1} y1={dimTopY - 5} x2={railDimX1} y2={dimTopY + 5} stroke={INK} strokeWidth={W_DIM} />
      <text x={(railDimX0 + railDimX1) / 2} y={dimTopY - 9} fill={INK} fontSize="11" textAnchor="middle">
        Cut to {cuts.railCutLabel}
      </text>
      </>
      )}

      {/* ── RAIL CUT PIECE — the welder's number, ABOVE the frame ──
          It takes the place the width dimension used to hold, because it is the
          figure the shop is actually working to. A callout rather than a
          dimension: it measures no distance on this drawing, it is the length of
          pipe to cut. The OTO width it comes from is dimensioned below the
          frame, well clear of it, so the two cannot be read as one pair.

          Shop copy only, actual size only — showRailCutPiece carries both. */}
      {cuts.showRailCutPiece && !hideDimensions && (
        <text
          x={(otoDimX0 + otoDimX1) / 2}
          y={cutPieceY}
          fill={INK}
          fontSize="11"
          fontWeight="700"
          textAnchor="middle"
        >
          RAIL CUT PIECE: {cuts.railCutPieceLabel}{isDouble ? ' (per leaf)' : ''}
        </text>
      )}

      {/* ── Width, BELOW the frame: an ACTUAL SIZE gate's OTO width ──
          Outside face to outside face, with its own line and ticks. On BOTH
          copies: the shop needs the finished size beside the cut, and the
          customer is signing for exactly this measurement. The frame's vertical
          budget already gave up the band this sits in (see availH), so it never
          crowds the frame and never reaches the sheet furniture below. */}
      {showWidthBelow && (
      <>
      <line x1={otoDimX0} y1={otoDimY} x2={otoDimX1} y2={otoDimY} stroke={INK} strokeWidth={W_DIM} />
      <line x1={otoDimX0} y1={otoDimY - 5} x2={otoDimX0} y2={otoDimY + 5} stroke={INK} strokeWidth={W_DIM} />
      <line x1={otoDimX1} y1={otoDimY - 5} x2={otoDimX1} y2={otoDimY + 5} stroke={INK} strokeWidth={W_DIM} />
      <text x={(otoDimX0 + otoDimX1) / 2} y={otoDimY + 14} fill={INK} fontSize="11" textAnchor="middle">
        {otoLabelText}
      </text>
      </>
      )}

      {/* Upright dimension, down the right side.
          Barbed wire splits it in two — the extension zone and the fence zone —
          because the shop reads 6' + 1' rather than one 84" figure. Without
          barbed wire this is the single upright-cut dimension, unchanged. */}
      {hideDimensions ? null : barbedWire ? (
        <>
          {/* Extension zone */}
          <line x1={dimRightX} y1={oy} x2={dimRightX} y2={fenceTopY} stroke={INK} strokeWidth={W_DIM} />
          <line x1={dimRightX - 5} y1={oy} x2={dimRightX + 5} y2={oy} stroke={INK} strokeWidth={W_DIM} />
          <line x1={dimRightX - 5} y1={fenceTopY} x2={dimRightX + 5} y2={fenceTopY} stroke={INK} strokeWidth={W_DIM} />
          <text
            x={dimRightX + 14}
            y={(oy + fenceTopY) / 2}
            fill={INK}
            fontSize="11"
            textAnchor="middle"
            transform={`rotate(-90 ${dimRightX + 14} ${(oy + fenceTopY) / 2})`}
          >
            {cuts.barbHeightLabel}
          </text>

          {/* Fence zone */}
          <line x1={dimRightX} y1={fenceTopY} x2={dimRightX} y2={oy + frameH} stroke={INK} strokeWidth={W_DIM} />
          <line x1={dimRightX - 5} y1={oy + frameH} x2={dimRightX + 5} y2={oy + frameH} stroke={INK} strokeWidth={W_DIM} />
          <text
            x={dimRightX + 14}
            y={(fenceTopY + oy + frameH) / 2}
            fill={INK}
            fontSize="11"
            textAnchor="middle"
            transform={`rotate(-90 ${dimRightX + 14} ${(fenceTopY + oy + frameH) / 2})`}
          >
            {cuts.fenceHeightLabel}
          </text>
        </>
      ) : (
        <>
          <line x1={dimRightX} y1={oy} x2={dimRightX} y2={oy + frameH} stroke={INK} strokeWidth={W_DIM} />
          <line x1={dimRightX - 5} y1={oy} x2={dimRightX + 5} y2={oy} stroke={INK} strokeWidth={W_DIM} />
          <line x1={dimRightX - 5} y1={oy + frameH} x2={dimRightX + 5} y2={oy + frameH} stroke={INK} strokeWidth={W_DIM} />
          <text
            x={dimRightX + 14}
            y={oy + frameH / 2}
            fill={INK}
            fontSize="11"
            textAnchor="middle"
            transform={`rotate(-90 ${dimRightX + 14} ${oy + frameH / 2})`}
          >
            {cuts.uprightCutLabel}
          </text>
        </>
      )}
    </svg>
  );
}

// ============================================================
// CANTILEVER SLIDE GATE
// ============================================================
// A separate render path — a slide gate is one long trussed frame with a
// counterbalance tail, sharing no geometry with a swing leaf. Every length
// comes from computeGateCuts; nothing is recomputed here.
//
// Logical coordinates run left-to-right with the OPENING first and the
// counterbalance last. X() mirrors that whole layout when the gate slides
// left, so the drawing flips without any text flipping with it.

// Layout budget for the cantilever, in viewBox units. Wider and shorter than a
// swing leaf, with extra room below for the dimension and the callout.
const C_PAD_LEFT = 20;
const C_PAD_RIGHT = 20;
const C_PAD_TOP = 50;
const C_PAD_BOTTOM = 90;
const C_DRAW_W = VIEW_W - C_PAD_LEFT - C_PAD_RIGHT;
const C_DRAW_H = VIEW_H - C_PAD_TOP - C_PAD_BOTTOM;

function renderCantilever({ cuts, uid, meshId, clipId, x, y, width, height, hideDimensions }) {
  const {
    totalLengthIn, endUprightCutIn, uprightPositionsIn,
    openingBayCount,
    railOdIn, uprightOdIn, diagonalOdIn,
    meshCoversCounterbalance,
    barbLeanDirection, barbedWire, barbExtensionIn,
    slideDirection, hangSide,
  } = cuts;

  const frameLenIn = totalLengthIn;
  const frameHIn = endUprightCutIn;

  // Arms lean with the slide on BOTH hang sides. Hung INSIDE the wire is on
  // the FAR face, so the whole assembly is shifted away from the slide and
  // drawn lighter — it reads as being behind the gate, not in front of it.
  const drawBarb = barbedWire && barbExtensionIn > 0;
  const farFace = hangSide === 'Inside';
  const barbExtIn = drawBarb ? barbExtensionIn : 0;
  // Lean is read in PAGE space straight from barbLeanDirection, so the mirror
  // below does not flip it.
  const pageLean = barbLeanDirection === 'Left' ? -1 : 1;

  // Slide Left mirrors the whole frame: the counterbalance moves to the left.
  const mirror = slideDirection === 'Left';

  // No horizontal allowance for the barbed wire: the arms lean toward the
  // counterbalance and are clamped to the frame, so the assembly can never
  // reach past either end. Reserving space for it only shrank the drawing.
  const contentWIn = frameLenIn;
  // Reserve whatever projects furthest above the top rail — the 12in barbed
  // wire extension or the 2in gate stops — so neither is clipped at the top.
  const topProjectionIn = Math.max(barbExtIn, GATE_STOP_IN);
  const contentHIn = frameHIn + topProjectionIn;
  const scale = (contentWIn > 0 && contentHIn > 0)
    ? Math.min(C_DRAW_W / contentWIn, C_DRAW_H / contentHIn)
    : 1;

  const frameW = frameLenIn * scale;
  const frameH = frameHIn * scale;
  const barbExt = barbExtIn * scale;
  // Drawn at gate scale, with a floor so a 2in stop still reads as a tube on a
  // long gate where the scale is small.
  const stopH = Math.max(GATE_STOP_IN * scale, MIN_TUBE_PX * 2);

  // Centre the frame plus its lean overhang; shift right when the lean would
  // otherwise spill off the left edge.
  const ox = C_PAD_LEFT + (C_DRAW_W - frameW) / 2;
  const contentTop = C_PAD_TOP + (C_DRAW_H - contentHIn * scale) / 2;
  const oy = contentTop + Math.max(barbExt, stopH);

  // Logical inches to page x, mirroring for a left-sliding gate.
  const X = (xIn) => ox + (mirror ? (frameLenIn - xIn) : xIn) * scale;
  const spanPx = (aIn, bIn) => {
    const a = X(aIn);
    const b = X(bIn);
    return { left: Math.min(a, b), w: Math.abs(b - a) };
  };

  // Members never draw thinner than MIN_TUBE_PX, so a 40 ft gate still shows
  // two distinct tube walls.
  const tube = (odIn) => Math.max(MIN_TUBE_PX, odIn * scale);
  const railT = tube(railOdIn);
  const upT = tube(uprightOdIn);

  const railTopY = oy;
  const railBotY = oy + frameH - railT;
  const innerTopY = oy + railT;
  const innerBotY = oy + frameH - railT;

  // Uprights: one at every bay line, both ends included. The ends run the FULL
  // height (outside of top rail to outside of bottom rail); interior uprights
  // sit between the rails, meeting their inside faces.
  // Positions come from computeGateCuts — the opening and the counterbalance
  // are divided separately, so the spacing can differ either side of the
  // boundary upright. Nothing is recomputed here.
  const positions = uprightPositionsIn && uprightPositionsIn.length
    ? uprightPositionsIn
    : [0, frameLenIn];
  const lastIdx = positions.length - 1;
  const uprights = positions.map((centerIn, i) => {
    const rawLeft = X(centerIn) - upT / 2;
    const left = Math.min(Math.max(rawLeft, ox), ox + frameW - upT);
    const isEnd = i === 0 || i === lastIdx;
    return { i, left, isEnd, centerIn, centerPx: left + upT / 2 };
  });
  const bayCount = Math.max(0, positions.length - 1);

  // The opening/counterbalance boundary IS an upright — index openingBayCount
  // in the computed positions — so mesh and barbed wire stop exactly on a bay
  // line with no snapping, and nothing is recomputed here.
  const boundaryIdx = Math.min(openingBayCount, lastIdx);
  const openingStopIn = positions[boundaryIdx] !== undefined
    ? positions[boundaryIdx]
    : frameLenIn;

  const meshEndIn = meshCoversCounterbalance ? frameLenIn : openingStopIn;
  const mesh = spanPx(0, meshEndIn);
  const meshY = innerTopY;
  const meshH = Math.max(0, innerBotY - innerTopY);

  // Members carry a WHITE fill, not `none`, so draw order produces real
  // occlusion: a rail drawn over a far-face arm has to hide it. White prints
  // as nothing, and the mesh is clipped to the opening between the rails, so
  // no line art is lost.
  const member = (mx, my, mw, mh, key) => (
    <rect key={key} x={mx} y={my} width={Math.max(0, mw)} height={Math.max(0, mh)}
      fill="#FFFFFF" stroke={INK} strokeWidth={W_MEMBER} />
  );

  // One diagonal per bay, alternating direction. Endpoints sit on the inside
  // faces of the two uprights and the two rails, so a diagonal terminates at
  // the frame and never crosses a member.
  //
  // ── WHERE THE ZIGZAG STARTS ──
  // The first diagonal begins at the TOP of the outermost end upright — the
  // frame end AWAY from the counterbalance, which is uprights[0] because the
  // logical run puts the opening first. Every following bay alternates from
  // there.
  //
  // ANCHORED TO THE FRAME, NOT TO THE PAGE. This used to be a bare
  // `i % 2 === 0`, which reads "start at the top of whichever upright happens
  // to be on the left of the page". That is the same thing only while the gate
  // slides right; a left-sliding gate is mirrored, its opening end is at the
  // page RIGHT, and the pattern came out reversed — the first diagonal ran into
  // the BOTTOM of the end upright instead of out of its top. Folding `mirror`
  // in is what makes the rule hold on both directions without naming a page
  // side anywhere.
  //
  // So: bay i starts at the top of uprights[i] and falls to the bottom of
  // uprights[i+1] when i is even, and the reverse when i is odd — stated in
  // frame order. `pageDown` is only that statement converted to page order.
  const diagonalW = Math.max(W_DIM, Math.min(W_MEMBER, diagonalOdIn * scale));
  const diagonals = Array.from({ length: bayCount }).map((_, i) => {
    const a = uprights[i];
    const b = uprights[i + 1];
    const xL = Math.min(a.left, b.left) + upT;
    const xR = Math.max(a.left, b.left);
    if (xR <= xL) return null;
    // From the top of the first upright of this bay, in FRAME order.
    const fromTopOfFirst = i % 2 === 0;
    // uprights[i] is on the page LEFT of uprights[i+1] unless the frame is
    // mirrored, in which case it is on the page right and the sense inverts.
    const pageDown = mirror ? !fromTopOfFirst : fromTopOfFirst;
    return (
      <line
        key={'diag-' + i}
        x1={xL} y1={pageDown ? innerTopY : innerBotY}
        x2={xR} y2={pageDown ? innerBotY : innerTopY}
        stroke={INK}
        strokeWidth={diagonalW}
      />
    );
  });

  // ── BARBED WIRE ──────────────────────────────────────────────────────
  // Drawn the way it is actually built, from the owner's photograph: short arms
  // standing up off the top rail, one above each upright, with three strands
  // stretched between them.
  //
  // WHAT THIS REPLACED, AND WHY. The strands used to be anchored on the upright
  // CENTRES while the arms were drawn a leg-gap to one side of those centres,
  // so no strand actually met an arm — each one floated a few units past the
  // first arm and stopped short of the last. Combined with a 45 degree lean it
  // read as one slanted band across the top of the gate, which on a flat
  // elevation looks like a perspective view. An elevation has no depth to show.
  //
  // Now every strand is anchored on the ARM AXIS at that strand's own height,
  // so it starts exactly on the first arm and finishes exactly on the last, and
  // the arms are drawn as two-walled members rather than single strokes. What
  // reads is discrete arms with wires between them.
  //
  // Barbed wire NEVER covers the counterbalance — opening portion only, on
  // every cantilever, manual or automatic.
  const barbEndIn = openingStopIn;
  const armUprights = drawBarb ? uprights.filter(u => u.centerIn <= barbEndIn + 1e-6) : [];
  // The assembly sits DIRECTLY on the frame — no horizontal offset, and full
  // opacity on both hang sides. Depth is communicated purely by draw order:
  // near-face wire is drawn after the rails (arms cross over them), far-face
  // wire is drawn before, so the rails interrupt the arms. See the two render
  // slots below.
  //
  // Every x is clamped to the frame so an arm tip or a strand end can never
  // reach past either end of the gate, even on a very short opening where the
  // 12in lean is large relative to the frame.
  const clampX = (v) => Math.min(Math.max(v, ox), ox + frameW);
  // Mounting-leg length: long enough to clear the top rail by an obvious
  // margin, and capped so it can never reach the bottom rail. The leg is what
  // makes the hang side legible — on the near face it runs visibly down the
  // frame, on the far face the rail is painted over it.
  const legLen = Math.min(
    Math.max(ARM_LEG_IN * scale, railT + ARM_LEG_MIN_CLEARANCE),
    Math.max(0, frameH - railT * 2 - 2)
  );
  const armT = tube(BARB_ARM_OD_IN);

  // The arm axis: base on the top face of the rail, tip barbExt above it,
  // displaced sideways by the lean. baseX is a leg-gap to the lean side of the
  // upright centre so the leg runs ALONGSIDE the upright and never over it.
  const arms = armUprights.map((u, i) => {
    const baseX = clampX(u.centerPx + pageLean * (upT / 2 + ARM_LEG_GAP));
    const tipX = clampX(baseX + pageLean * barbExt * BARB_LEAN_RATIO);
    return { i, baseX, tipX, tipY: oy - barbExt };
  });
  // Where a given arm's axis sits h units above the rail. This is the one
  // function that decides both the arm outline and the strand ends, which is
  // what guarantees a strand lands on an arm instead of near it.
  const armAxisX = (arm, h) => arm.baseX + (arm.tipX - arm.baseX) * (barbExt > 0 ? h / barbExt : 0);

  const firstArm = arms.length ? arms[0] : null;
  const lastArm = arms.length ? arms[arms.length - 1] : null;

  const barbGroup = drawBarb && arms.length > 1 ? (
    <g>
      {arms.map(arm => {
        // One member: mounting leg down the frame face, a knee at the rail, then
        // the leaning arm. Drawn as a closed outline with a WHITE fill, exactly
        // like every other member here, so it occludes what it is drawn over
        // and shows two walls rather than one hairline.
        const a = armT / 2;
        const dx = arm.tipX - arm.baseX;
        const dy = arm.tipY - oy;                       // negative: the arm rises
        const len = Math.hypot(dx, dy) || 1;
        // Perpendicular to the arm axis, so the tip is cut square across the
        // member rather than horizontally.
        const px = (-dy / len) * a;
        const py = (dx / len) * a;
        const legBottom = oy + legLen;
        const pts = [
          [arm.baseX - a, legBottom],
          [arm.baseX - a, oy],
          [arm.tipX - px, arm.tipY - py],
          [arm.tipX + px, arm.tipY + py],
          [arm.baseX + a, oy],
          [arm.baseX + a, legBottom],
        ];
        return (
          <polygon
            key={'arm-' + arm.i}
            points={pts.map(q => q[0] + ',' + q[1]).join(' ')}
            fill="#FFFFFF"
            stroke={INK}
            strokeWidth={W_MEMBER}
          />
        );
      })}

      {/* Three strands, evenly up the arm's height, each running from the first
          arm to the last AT ITS OWN HEIGHT — so it terminates on both end arms
          and overhangs neither. The top strand lands on the arm tips, which is
          where the top wire actually sits. */}
      {Array.from({ length: BARB_STRANDS }).map((_, sIdx) => {
        const h = (barbExt * (sIdx + 1)) / BARB_STRANDS;
        const sy = oy - h;
        const sx0 = armAxisX(firstArm, h);
        const sx1 = armAxisX(lastArm, h);
        const left = Math.min(sx0, sx1);
        const right = Math.max(sx0, sx1);
        const span = right - left;
        const half = BARB_SIZE / 2;
        // Barbs inset a half-barb at each end so no X hangs off an arm.
        const usable = span - BARB_SIZE;
        const n = usable > 0 ? Math.max(1, Math.floor(span / BARB_SPACING)) : 0;
        return (
          <g key={'cstrand-' + sIdx}>
            <line x1={sx0} y1={sy} x2={sx1} y2={sy} stroke={INK} strokeWidth={W_BARB} />
            {Array.from({ length: n }).map((__, b) => {
              const cx = left + half + (usable * (b + 0.5)) / n;
              return (
                <g key={'cbarb-' + b}>
                  <line x1={cx - half} y1={sy - half} x2={cx + half} y2={sy + half} stroke={INK} strokeWidth={W_BARB} />
                  <line x1={cx - half} y1={sy + half} x2={cx + half} y2={sy - half} stroke={INK} strokeWidth={W_BARB} />
                </g>
              );
            })}
          </g>
        );
      })}
    </g>
  ) : null;

  // TWO horizontal dimensions: the overall length ABOVE the frame, and the drawn
  // opening BELOW it. No counterbalance dimension, no height — the owner has
  // been explicit that the counterbalance is not dimensioned.
  //
  // The opening is measured OUTSIDE face to OUTSIDE face: from the outer face of the end
  // upright to the outer face of the upright closing the opening — not centre
  // to centre. The LABEL stays drawnOpeningLabel (the shop's number, carrying
  // the 6" shift); only the tick placement is geometric.
  const endUpright = uprights[0];
  const boundaryUpright = uprights[boundaryIdx] || uprights[uprights.length - 1];
  const dimA = mirror ? endUpright.left + upT : endUpright.left;
  const dimB = mirror ? boundaryUpright.left : boundaryUpright.left + upT;
  const openDim = { left: Math.min(dimA, dimB), w: Math.abs(dimB - dimA) };
  const dimY = oy + frameH + 34;

  // ── OVERALL LENGTH — ABOVE THE FRAME, END TO END ──
  // Opening plus counterbalance: ticks on the two outer faces of the frame.
  //
  // The TICKS are the computed frame, always. The NUMBER is
  // cuts.overallLengthLabel, which is the owner's typed figure when there is
  // one. Nothing in this renderer reads a typed length — frameLenIn is
  // totalLengthIn — so an override moves the text and nothing else.
  //
  // contentTop is already the top of whatever projects highest above the rail
  // (a gate stop, or a barb arm tip), so a fixed gap above it can cross neither.
  const overallDimY = contentTop - 16;
  const overallDimL = ox;
  const overallDimR = ox + frameW;

  // NO HEIGHT DIMENSION ON THE DRAWING, deliberately.
  //
  // Two stacked vertical dimensions for the fence zone and the barbed wire
  // extension were tried here and removed: this sheet carries only horizontal
  // dimensions — the overall length and the drawn opening — and a reading down the side
  // fought both that and the dimension style of the rest of the drawing.
  //
  // The fact they were there to state — that a barbed cantilever finishes a
  // foot taller than the height the user typed — is now a text label in the top
  // right corner of the sheet, opposite QTY. See GateSheet.js. It is a caption,
  // not a measurement, which is what it always should have been.

  // Callout: a leader to one END upright, on the counterbalance side so it
  // never collides with the opening dimension.
  const endU = uprights[uprights.length - 1];
  const targetX = endU ? endU.centerPx : ox + frameW;
  const targetY = oy + frameH / 2;
  // The leader runs INWARD from the end upright and the label is centred on
  // its landing point, so the text grows toward the middle of the sheet and
  // can never be clipped at the edge (the previous outward-anchored label ran
  // off the right side). Clamped as a belt-and-braces guard.
  const inward = targetX > VIEW_W / 2 ? -1 : 1;
  const CALLOUT_HALF_W = 40;
  const leadX = Math.min(
    Math.max(targetX + inward * 48, CALLOUT_HALF_W + 10),
    VIEW_W - CALLOUT_HALF_W - 10
  );
  const leadY = oy + frameH + 62;

  return (
    <svg
      x={x}
      y={y}
      width={width}
      height={height}
      viewBox={'0 0 ' + VIEW_W + ' ' + VIEW_H}
      role="img"
      aria-label={'Cantilever slide gate cut sheet, slides ' + (slideDirection || '') + ', ' + bayCount + ' bays'
        + (cuts.customerCopy ? '' : ', end upright ' + (cuts.endUprightLabel || ''))}
      style={{ display: 'block', fontFamily: 'Manrope, sans-serif' }}
    >
      <defs>
        <pattern id={meshId} width="8" height="8" patternUnits="userSpaceOnUse">
          <path d="M0,8 L8,0" stroke={INK} strokeWidth={W_MESH} strokeOpacity="0.65" fill="none" />
          <path d="M0,0 L8,8" stroke={INK} strokeWidth={W_MESH} strokeOpacity="0.65" fill="none" />
        </pattern>
        <clipPath id={clipId + '-cant'}>
          <rect x={mesh.left} y={meshY} width={mesh.w} height={meshH} />
        </clipPath>
      </defs>

      {/* Mesh — clipped inside the rails, spanning either the opening portion
          or the whole frame depending on Manual vs Automatic. */}
      <rect x={mesh.left} y={meshY} width={mesh.w} height={meshH}
        fill={'url(#' + meshId + ')'} clipPath={'url(#' + clipId + '-cant)'} />

      {/* Diagonals first, so the members read over them at the joints. */}
      {diagonals}

      {/* FAR-FACE barbed wire (hung Inside): drawn BEFORE the rails, so the
          rails are painted over it and interrupt the arms where they cross.
          The arm bases disappear behind the top rail. That occlusion is what
          says 'behind the gate' — nothing is faded. */}
      {farFace && barbGroup}

      {/* Top and bottom rails — full length */}
      {member(ox, railTopY, frameW, railT, 'c-rail-top')}
      {member(ox, railBotY, frameW, railT, 'c-rail-bottom')}

      {/* Uprights — ends full height, interior between the rails */}
      {uprights.map(u => (
        u.isEnd
          ? member(u.left, oy, upT, frameH, 'c-up-' + u.i)
          : member(u.left, innerTopY, upT, Math.max(0, innerBotY - innerTopY), 'c-up-' + u.i)
      ))}

      {/* Gate stops — a short pipe upright on each END upright, aligned with it
          and projecting above the top rail. Drawn on the FRAME layer, so the
          barbed wire's near/far ordering still holds: a near-face arm passes in
          front of a stop, a far-face arm behind it. No label, no leader. */}
      {uprights.filter(u => u.isEnd).map(u => (
        member(u.left, oy - stopH, upT, stopH, 'c-stop-' + u.i)
      ))}

      {/* NEAR-FACE barbed wire (hung Outside): drawn AFTER the members, so the
          arms cross visibly over the top rail. */}
      {!farFace && barbGroup}

      {!hideDimensions && (
        <>
          {/* Overall length — above the frame, the full length end to end.
              Kept on a customer copy: the size of the gate is exactly what the
              customer is approving. */}
          <g data-dim="overall">
            <line x1={overallDimL} y1={overallDimY} x2={overallDimR} y2={overallDimY} stroke={INK} strokeWidth={W_DIM} />
            <line x1={overallDimL} y1={overallDimY - 5} x2={overallDimL} y2={overallDimY + 5} stroke={INK} strokeWidth={W_DIM} />
            <line x1={overallDimR} y1={overallDimY - 5} x2={overallDimR} y2={overallDimY + 5} stroke={INK} strokeWidth={W_DIM} />
            <text x={(overallDimL + overallDimR) / 2} y={overallDimY - 6} fill={INK} fontSize="11" textAnchor="middle">
              {cuts.overallLengthLabel}
            </text>
          </g>

          {/* Drawn opening — below the frame, unchanged */}
          <line x1={openDim.left} y1={dimY} x2={openDim.left + openDim.w} y2={dimY} stroke={INK} strokeWidth={W_DIM} />
          <line x1={openDim.left} y1={dimY - 5} x2={openDim.left} y2={dimY + 5} stroke={INK} strokeWidth={W_DIM} />
          <line x1={openDim.left + openDim.w} y1={dimY - 5} x2={openDim.left + openDim.w} y2={dimY + 5} stroke={INK} strokeWidth={W_DIM} />
          <text x={openDim.left + openDim.w / 2} y={dimY - 8} fill={INK} fontSize="11" textAnchor="middle">
            {cuts.drawnOpeningLabel}
          </text>


          {/* End-upright callout — leader and label together. Dropped on a
              CUSTOMER COPY: a cut length is shop detail, and a customer
              signing off on the gate's appearance only gets confused by it.
              The opening dimension above stays, because that IS what they
              are approving. */}
          {!cuts.customerCopy && (
            <>
              <line x1={targetX} y1={targetY} x2={leadX} y2={leadY} stroke={INK} strokeWidth={W_DIM} />
              <text x={leadX} y={leadY + 4} fill={INK} fontSize="11" textAnchor="middle">
                {cuts.endUprightLabel}
              </text>
            </>
          )}
        </>
      )}
    </svg>
  );
}
